import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";
import Navbar from "../components/Navbar";
import { ArrowRight, Briefcase, Building2, PartyPopper, AlertCircle, ClipboardCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  {
    v: "agent",
    icon: <Briefcase className="w-5 h-5" />,
    t: "Travel Agent",
    d: "Post RFPs · Get bids in minutes · Book with confidence.",
    pending: false,
  },
  {
    v: "dmc",
    icon: <Building2 className="w-5 h-5" />,
    t: "DMC Partner",
    d: "Receive matched leads · Bid to win · Escrow-secured payouts.",
    pending: true,
  },
  {
    v: "mice_operator",
    icon: <PartyPopper className="w-5 h-5" />,
    t: "MICE Operator",
    d: "Bid on group / conference / incentive procurement RFPs.",
    pending: true,
  },
];

function PendingScreen({ user }) {
  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="register-pending">
      <Navbar />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl card-paper p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-verified text-white">
            <ClipboardCheck className="w-7 h-7" />
          </div>
          <p className="overline mt-6">Application received</p>
          <h1 className="font-display text-4xl font-black tracking-tight text-ink mt-3">
            Under review · <span className="text-brand">24–48 hours</span>
          </h1>
          <p className="text-muted mt-4 max-w-lg mx-auto leading-relaxed">
            Thanks {user?.name?.split(" ")[0]}. Trawell Ops is reviewing your registration details.
            You'll get an email at <span className="text-ink font-mono">{user?.email}</span> the moment
            you're approved and can start receiving matched RFPs and submitting bids.
          </p>
          <div className="pending-banner mt-8 text-left inline-block">
            <p className="font-mono text-xs uppercase tracking-widest mb-2">What's next</p>
            <ol className="text-sm space-y-1.5 list-decimal pl-5">
              <li>Ops team reviews your company profile</li>
              <li>KYC + partnership agreement in a follow-up email</li>
              <li>Account activated · bidding unlocked</li>
            </ol>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/dashboard" className="btn-primary" data-testid="pending-open-dashboard">
              Preview dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:ops@trawelldmc.com" className="btn-ghost">Contact Ops</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [role, setRole] = useState(loc.state?.role || "agent");
  const [form, setForm] = useState({
    name: "", email: "", password: "", company: "", country: "", phone: "", website: "",
    specialties: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const specialtiesArr = form.specialties.split(",").map((s) => s.trim()).filter(Boolean);
      const u = await register({ ...form, role, specialties: specialtiesArr });
      const needsReview = role !== "agent";
      if (needsReview) {
        setPending(u);
      } else {
        toast.success(`Welcome to TrawellB2B, ${u.name.split(" ")[0]}`);
        nav("/dashboard", { replace: true });
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (pending) return <PendingScreen user={pending} />;

  const current = ROLES.find((r) => r.v === role);

  return (
    <div className="min-h-screen bg-paper" data-testid="register-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-14">
        <p className="overline">Join the TrawellB2B network</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink mt-3">
          Pick your <span className="text-brand">side</span>.
        </h1>
        <p className="text-sm text-muted mt-2">
          Already a member? <Link to="/login" className="text-brand hover:underline">Sign in</Link>
        </p>

        {/* Role switcher (3 roles) */}
        <div className="grid md:grid-cols-3 gap-3 mt-8" data-testid="role-switcher">
          {ROLES.map((r) => (
            <button
              key={r.v}
              onClick={() => setRole(r.v)}
              type="button"
              className={`text-left p-5 border rounded-md transition-all bg-white ${
                role === r.v ? "border-brand shadow-[0_0_0_3px_var(--brand-glow)]" : "border-hairline hover:border-ink"
              }`}
              data-testid={`role-${r.v}`}
            >
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${role === r.v ? "bg-brand text-white" : "bg-ledger text-ink"}`}>
                {r.icon}
              </div>
              <h3 className="font-display text-ink text-lg font-bold mt-3">{r.t}</h3>
              <p className="text-xs text-muted mt-1 leading-relaxed">{r.d}</p>
              {r.pending && (
                <p className="text-[10px] font-mono uppercase tracking-widest text-alert mt-3 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Ops review · 24–48h
                </p>
              )}
            </button>
          ))}
        </div>

        {current?.pending && (
          <div className="pending-banner mt-6 flex items-start gap-3" data-testid="pending-notice">
            <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Verification required.</strong> For <strong>{current.t}</strong> registrations, Trawell Ops reviews
              company details within 24–48 hrs before activating bidding. KYC documents + partnership agreement follow via email.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-8" data-testid="register-form">
          <div>
            <label className="overline">Full name</label>
            <input required value={form.name} onChange={upd("name")} className="w-full mt-2 px-4 py-3" data-testid="register-name" />
          </div>
          <div>
            <label className="overline">{role === "agent" ? "Agency" : "Company"}</label>
            <input required={role !== "agent"} value={form.company} onChange={upd("company")} className="w-full mt-2 px-4 py-3" data-testid="register-company" />
          </div>
          <div>
            <label className="overline">Work email</label>
            <input type="email" required value={form.email} onChange={upd("email")} className="w-full mt-2 px-4 py-3" data-testid="register-email" />
          </div>
          <div>
            <label className="overline">Country</label>
            <input value={form.country} onChange={upd("country")} className="w-full mt-2 px-4 py-3" data-testid="register-country" placeholder="e.g. United Arab Emirates" />
          </div>
          <div>
            <label className="overline">Phone / WhatsApp</label>
            <input value={form.phone} onChange={upd("phone")} className="w-full mt-2 px-4 py-3" data-testid="register-phone" placeholder="+971 ..." />
          </div>
          <div>
            <label className="overline">Website</label>
            <input value={form.website} onChange={upd("website")} className="w-full mt-2 px-4 py-3" data-testid="register-website" placeholder="https://" />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Specialties · comma-separated</label>
            <input value={form.specialties} onChange={upd("specialties")} className="w-full mt-2 px-4 py-3" data-testid="register-specialties" placeholder={role === "mice_operator" ? "Conferences, Incentives, Gala Dinners" : role === "dmc" ? "Luxury, Honeymoon, Adventure" : "MICE, Corporate, Leisure"} />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Password · min 6 chars</label>
            <input type="password" required minLength={6} value={form.password} onChange={upd("password")} className="w-full mt-2 px-4 py-3" data-testid="register-password" />
          </div>
          {err && (
            <p className="sm:col-span-2 text-sm text-red-600 flex items-center gap-2" data-testid="register-error">
              <AlertCircle className="w-4 h-4" /> {err}
            </p>
          )}
          <button type="submit" disabled={busy} className="btn-primary sm:col-span-2 justify-center" data-testid="register-submit">
            {busy ? "Creating account…" : (<>{current?.pending ? "Submit for review" : "Create account"} <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <p className="sm:col-span-2 text-xs text-muted text-center">
            By registering you agree to the Trawell partnership terms. KYC documents follow via email.
          </p>
        </form>
      </div>
    </div>
  );
}
